import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser, Location as Location_2 } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { toObservable } from '@angular/core/rxjs-interop';

import { map, Observable, of, Subscription, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import { v4 as uuid } from 'uuid';

import { LocationPair } from '@fcom/common/store';
import { HotjarCaptureEvent, HotjarService, ThirdPartyAncillariesProvider, TripType } from '@fcom/common';
import { ButtonMode, ButtonSize, ButtonTheme, LoaderTheme, TabTheme } from '@fcom/ui-components';
import { ConfigService, LocalDate, NativeBridgeService } from '@fcom/core';
import { FINNISH_SITES } from '@fcom/core/constants';
import { unsubscribe } from '@fcom/core/utils';
import { LoginStatus } from '@fcom/core-api/login';
import { BookingAndAppState } from '@fcom/common/interfaces/booking';
import { casProfileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors/login.selector';
import { LanguageService } from '@fcom/ui-translate';
import { finShare } from '@fcom/rx';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { WidgetLayout, WidgetTab } from '../../interfaces';
import { ANIMATION_DURATION } from '../../constants';
import { BookingWidgetService } from '../../services/booking-widget.service';
import { BookingWidgetTripService } from '../../services/booking-widget-trip.service';

function isWidgetTab(tab: unknown): tab is WidgetTab {
  return Boolean(Object.values(WidgetTab).includes(tab as WidgetTab));
}

@Component({
  selector: 'fin-booking-widget',
  templateUrl: './booking-widget.component.html',
  styleUrl: './booking-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('smoothGrow', [
      transition('* => *', [
        animate(
          `${ANIMATION_DURATION}ms cubic-bezier(0.4, 0.0, 0.2, 1)`,
          keyframes([style({ height: '{{ startHeight }}' }), style({ height: '*' })])
        ),
      ]),
    ]),
  ],
  standalone: false,
})
export class BookingWidgetComponent implements OnInit, OnDestroy, AfterViewInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private hotjarService = inject(HotjarService);
  private bookingWidgetService = inject(BookingWidgetService);
  private location = inject(Location_2);
  private configService = inject(ConfigService);
  private bookingWidgetTripService = inject(BookingWidgetTripService);
  private store$ = inject<Store<BookingAndAppState>>(Store);
  private languageService = inject(LanguageService);
  private platform = inject(PLATFORM_ID);
  private nativeBridgeService = inject(NativeBridgeService);
  private themeService = inject(FinnairThemeService);

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly TripType = TripType;
  readonly TabTheme = TabTheme;
  readonly WidgetTab = WidgetTab;
  readonly LoginStatus = LoginStatus;
  readonly LoaderTheme = LoaderTheme;
  readonly WidgetLayout = WidgetLayout;
  readonly ThirdPartyAncillariesProvider = ThirdPartyAncillariesProvider;

  readonly defaultLocations = input<LocationPair[]>([]);

  readonly headingLevel = input<1 | 2>(1);

  readonly enableAurinkomatkat = input(true);

  readonly enableAward = input(true);

  readonly enablePromoCode = input(true);

  readonly initialTripType = input<TripType>(undefined);

  readonly layout = input<WidgetLayout>(WidgetLayout.DEFAULT);

  readonly compactMode = input(false);

  readonly widgetRoot: Signal<ElementRef | undefined> = viewChild('widgetRoot');

  readonly activeTab = this.bookingWidgetTripService.activeTab;
  readonly loginStatus = this.bookingWidgetService.loginStatus;
  readonly isInsideReactNativeWebView = this.nativeBridgeService.isInsideReactNativeWebView;
  //TODO: remove the checker when the multicity e2e test ready
  // Checks whether there is any location history saved (i.e. whether it is the initial render or not)
  readonly isFrontPage = computed(() => this.defaultLocations().length === 0);
  readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  rootContainerStartHeight$: Observable<string> = of('auto');
  isInitialRender: boolean;
  subscription = new Subscription();
  isAm$: Observable<boolean>;
  calendarIdentifier: string;
  showCompact$ = of(true);
  private readonly _showCompact$ = toObservable(this.bookingWidgetService.showCompact);
  titleText$: Observable<string>;
  h1Style$: Observable<{ display?: string }>;

  isAMLocale: boolean;
  showHotelsTab: boolean;
  showCarTrawlerTab: boolean;
  showCarRentalSearch: boolean;
  showHotelSearch: boolean;

  ngOnInit(): void {
    // Set up tab visibility based on configuration, inputs, and locale
    this.isAMLocale = FINNISH_SITES.includes(this.languageService.langValue);

    this.showHotelsTab =
      (this.isAMLocale && this.enableAurinkomatkat()) || // Finnish locale: show AM tab
      (!this.isAMLocale &&
        this.configService.cfg.enableHotelsBookingWidget &&
        this.configService.cfg.enableBwThirdPartyAncillaries); // Non-Finnish: show Hotels tab

    this.showCarTrawlerTab = this.configService.cfg.enableBwThirdPartyAncillaries;
    this.showCarRentalSearch = this.configService.cfg.enableBwCarRentalSearch;
    this.showHotelSearch = this.configService.cfg.enableBwHotelSearch;
    this.bookingWidgetService.setUsePopoverSelectors(false);
    this.calendarIdentifier = uuid();
    const activeTabFromRouteParams = this.route.snapshot?.queryParams['tab'] ?? this.route.snapshot?.data.tab;
    if (activeTabFromRouteParams && Object.values(WidgetTab).includes(activeTabFromRouteParams)) {
      this.bookingWidgetTripService.setActiveTab(activeTabFromRouteParams);
      this.router.navigate([], {
        queryParams: { tab: null },
        queryParamsHandling: 'merge',
      });
    }

    const casProfile$ = this.store$.pipe(casProfileOrUndefinedIfNotLoggedIn(), finShare());

    this.titleText$ = casProfile$.pipe(
      switchMap((casProfile) => {
        if (casProfile?.firstName) {
          const personalisedLabelKey =
            casProfile.previousLoginDate &&
            new LocalDate(casProfile.previousLoginDate.substring(0, 10)).lt(LocalDate.now().minusMonths(6))
              ? 'bookingSearch.personalisedTitle.withoutRecentLogin'
              : 'bookingSearch.personalisedTitle.withRecentLogin';

          return this.languageService.translate(personalisedLabelKey, {
            name: casProfile.firstName,
          });
        }
        return this.languageService.translate('bookingSearch.personalisedTitle.loggedOut');
      })
    );

    this.h1Style$ = isPlatformBrowser(this.platform)
      ? this.titleText$.pipe(map(() => ({ display: 'inline' })))
      : of({ display: 'none' });

    this.showCompact$ = this._showCompact$.pipe(map((showCompact) => showCompact && this.compactMode()));

    // This is to check whether there is any location history saved (if it is the initial render or not)
    // This is coupled with the compact widget behavior since we only want to compact mode to happen on first render of the app.
    const isInitialRender = (this.location.getState() as { navigationId: number })?.navigationId === 1;
    if (isInitialRender) {
      this.bookingWidgetService.resetSelection();
      this.bookingWidgetService.setCompactWidget(true);
    }

    if (this.initialTripType()) {
      this.bookingWidgetTripService.setTripType(this.initialTripType());
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platform)) {
      // To animate the height transition properly we need to have some specific value what the transition start is based on
      this.rootContainerStartHeight$ = this.showCompact$.pipe(
        map(() => {
          const widgetRoot = this.widgetRoot();
          const height = widgetRoot ? (widgetRoot.nativeElement as HTMLElement).getBoundingClientRect().height : 0;
          return height ? `${height}px` : 'auto';
        })
      );
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  setActiveTab({ data }: { index: number; data: unknown }): void {
    if (isWidgetTab(data)) {
      this.bookingWidgetTripService.setActiveTab(data);
    }
  }

  startHotjarCapture(): void {
    this.subscription.add(this.hotjarService.startCapture(HotjarCaptureEvent.BOOKING_WIDGET).subscribe());
  }

  onAmLocationChanged(): void {
    this.bookingWidgetService.expandCompactWidget();
  }
}
